import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { getToken } from '../services/auth';

export const useAdmin = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (authLoading) return;

      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        // Try to fetch admin status from the backend
        const token = await getToken();
        if (!token) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        try {
          const response = await fetch('/api/v1/admin/check', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setIsAdmin(data.isAdmin);
          } else if (response.status === 401) {
            // Authentication error - token might be expired
            console.error('Authentication error when checking admin status');
            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('id_token');
            sessionStorage.removeItem('user');
            
            // Notify user they need to log in again
            alert('Your session has expired. Please log in again.');
            window.location.href = '/login';
            return;
          } else {
            // Fallback to client-side check in case of API failure
            console.warn('Admin check API failed, falling back to client-side check');
            
            // Client-side fallback (using environment variable defaults)
            const adminEmails = ['admin@askceo.ai'];
            const adminDomains = ['askceo.ai'];
            
            // Check if user has admin rights based on email or domain
            const hasAdminEmail = user.email && adminEmails.includes(user.email);
            const hasAdminDomain = user.email && adminDomains.some(domain => user.email?.endsWith(`@${domain}`));
            const hasAdminRole = user.user_metadata?.roles?.includes('admin');
            
            setIsAdmin(hasAdminEmail || hasAdminDomain || hasAdminRole);
          }
        } catch (fetchError) {
          console.error('Network error checking admin status:', fetchError);
          // Fallback to client-side check
          const adminEmails = ['admin@askceo.ai'];
          const adminDomains = ['askceo.ai'];
          
          const hasAdminEmail = user.email && adminEmails.includes(user.email);
          const hasAdminDomain = user.email && adminDomains.some(domain => user.email?.endsWith(`@${domain}`));
          const hasAdminRole = user.user_metadata?.roles?.includes('admin');
          
          setIsAdmin(hasAdminEmail || hasAdminDomain || hasAdminRole);
        }
      } catch (error) {
        console.error('Failed to check admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [user, authLoading]);

  return { isAdmin, loading };
};
